/**
 * Terrain render pass — flat XZ UV grid displaced in the vertex shader by sampling
 * the heightmap texture. Camera UBO comes from frame.ts at group(0); this pass owns
 * group(1) (terrain params + heightmap texture view).
 */

import { config } from '../config.ts';
import { createRenderPipeline } from '../gpu/pipelines.ts';
import { createUniformBuffer, writeUniform } from '../gpu/buffers.ts';
import type { TerrainGeometry } from './terrain-geometry.ts';
import shadowPcfWgsl from './shadow_pcf.wgsl?raw';
import fogWgsl from './fog.wgsl?raw';
import terrainWgsl from './terrain.wgsl?raw';

const wgsl = terrainWgsl + '\n' + shadowPcfWgsl + '\n' + fogWgsl;

export interface TerrainPass {
  draw(pass: GPURenderPassEncoder): void;
  destroy(): void;
}

export interface TerrainPassOptions {
  presentationFormat: GPUTextureFormat;
  depthFormat: GPUTextureFormat;
  heightmapView: GPUTextureView;
  biomeView: GPUTextureView;
  roadMaskView: GPUTextureView;
  cameraBindGroupLayout: GPUBindGroupLayout;
  dayNightBindGroupLayout: GPUBindGroupLayout;
  shadowBindGroupLayout: GPUBindGroupLayout;
  /** Vertex + index buffer shared with the shadow pass. Owned by frame.ts. */
  geometry: TerrainGeometry;
}

// vec4<f32> params: worldSize, worldHeight, heightmapSize, unused
const TERRAIN_UBO_SIZE = 16;

export function createTerrainPass(device: GPUDevice, opts: TerrainPassOptions): TerrainPass {
  // --- Terrain UBO
  const terrainUbo = createUniformBuffer(device, TERRAIN_UBO_SIZE, 'terrain.params');
  writeUniform(device, terrainUbo, new Float32Array([
    config.worldSize,
    config.worldHeight,
    config.heightmapSize,
    0,
  ]));

  // --- Bind group layout for group(1)
  const terrainBgl = device.createBindGroupLayout({
    label: 'terrain.bgl',
    entries: [
      {
        binding: 0,
        visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
        buffer: { type: 'uniform', minBindingSize: TERRAIN_UBO_SIZE },
      },
      {
        binding: 1,
        visibility: GPUShaderStage.VERTEX,
        // r32float is not filterable — must declare as unfilterable-float.
        texture: { sampleType: 'unfilterable-float', viewDimension: '2d' },
      },
      {
        binding: 2,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: 'uint', viewDimension: '2d' },
      },
      {
        binding: 3,
        visibility: GPUShaderStage.FRAGMENT,
        // Road mask is r32float (see gpu/formats.ts), which is not filterable.
        texture: { sampleType: 'unfilterable-float', viewDimension: '2d' },
      },
    ],
  });

  const terrainBindGroup = device.createBindGroup({
    label: 'terrain.bg',
    layout: terrainBgl,
    entries: [
      { binding: 0, resource: { buffer: terrainUbo } },
      { binding: 1, resource: opts.heightmapView },
      { binding: 2, resource: opts.biomeView },
      { binding: 3, resource: opts.roadMaskView },
    ],
  });

  const pipelineLayout = device.createPipelineLayout({
    label: 'terrain.pll',
    bindGroupLayouts: [
      opts.cameraBindGroupLayout,
      terrainBgl,
      opts.dayNightBindGroupLayout,
      opts.shadowBindGroupLayout,
    ],
  });

  const pipeline = createRenderPipeline(device, wgsl, {
    label: 'terrain',
    layout: pipelineLayout,
    buffers: [{
      arrayStride: 2 * 4,
      attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }],
    }],
    targets: [{ format: opts.presentationFormat }],
    primitive: { topology: 'triangle-list', cullMode: 'back', frontFace: 'ccw' },
    depthStencil: {
      format: opts.depthFormat,
      depthCompare: 'less',
      depthWriteEnabled: true,
    },
  });

  return {
    draw(pass) {
      pass.setPipeline(pipeline);
      pass.setBindGroup(1, terrainBindGroup);
      pass.setVertexBuffer(0, opts.geometry.vertexBuffer);
      pass.setIndexBuffer(opts.geometry.indexBuffer, opts.geometry.indexFormat);
      pass.drawIndexed(opts.geometry.indexCount);
    },
    destroy() {
      terrainUbo.destroy();
    },
  };
}
